import React, { useEffect, useState } from 'react';

import moment from 'moment';
import DataTable from 'react-data-table-component';
import { Card, CardBody, CardFooter, CardHeader, Col, Row } from 'reactstrap';

import { TablePagination } from '../../../../../../Common';
import { Constant } from '../../../../../../Helpers/constant';
import { formattedWithComma, hasPermission } from '../../../../../../Helpers/Helpers';
import { PermissionConstants } from '../../../../../../Helpers/Constants/permissions';

const Table = props => {
  const [currentFilteredBookingTitle, setCurrentFilterBookingTitle] = useState('Bookings');

  /* ---------------------------- LOCAL STATES ---------------------------- */
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    if (props?.data?.length > 0) {
      getBookingStatusTitleHandler(parseInt(props?.appliedFilters?.status));

      const fetchColums = [
        {
          name: 'S.No',
          selector: row => <span className="fw-medium">{row?.serialNumber}</span>,
          width: '90px',
        },
        {
          name: 'Booking ID',
          selector: row => row?.bookingId,
          wrap: true,
          width: '180px',
        },
        {
          name: 'Facility Name',
          selector: row => row?.facilityName,
          wrap: true,
          width: '250px',
        },
        {
          name: 'Name',
          selector: row => row?.name,
          wrap: true,
          width: '170px',
        },
        {
          name: 'Mobile No.',
          selector: row => row?.mobile,
          wrap: true,
          width: '150px',
        },
        {
          name: 'Email',
          selector: row => row?.email,
          width: '220px',
          wrap: true,
        },
        {
          name: 'Total Amount',
          selector: row => row?.fees,
          width: '140px',
          wrap: true,
        },
        {
          name: 'Paid Amount',
          selector: row => row?.totalPayable,
          width: '140px',
          wrap: true,
        },
        {
          name: 'Discount Amount',
          selector: row => row?.dicounts,
          width: '140px',
          wrap: true,
        },
        {
          name: 'Benefit Type',
          selector: row => row?.benefitType,
          width: '140px',
          wrap: true,
        },
        {
          name: 'Partner',
          selector: row => row?.partnerName,
          width: '250px',
          wrap: true,
        },
        {
          name: 'Service',
          selector: row => row?.serviceName,
          width: '220px',
          wrap: true,
        },
        {
          name: 'Payment ID',
          selector: row => row?.paymentId,
          width: '300px',
          wrap: true,
        },
        {
          name: 'Membership Id',
          selector: row => row?.membershipId,
          width: '180px',
          wrap: true,
        },
        {
          name: 'Created At',
          selector: row => row?.createdAt,
          width: '190px',
          wrap: true,
        },
        {
          name: 'Updated At',
          selector: row => row?.updatedAt,
          width: '190px',
          wrap: true,
        },
        {
          name: 'Payment Status',
          selector: row => row?.paymentStatus,
          width: '190px',
          wrap: true,
        },
        {
          name: 'Status',
          width: '160px',
          cell: row => (
            <span
              className={`booking_status badge break-word bg-${
                Constant.BOOKING_STATUSES.filter(status => status?.name === row?.bookingStatus)[0]?.bgColor
              }`}
            >
              {row.bookingStatus}
            </span>
          ),
        },
      ];
      setColumns(fetchColums);

      setData(
        props?.data?.map((data, index) => {
          const status = Constant?.BOOKING_STATUSES?.filter(statusObj => statusObj.objectId === data?.bookingStatusCode)[0]?.name;

          return {
            serialNumber: `${props.pageNumber * Constant.TABLE_PAGE_SIZE + (index + 1)}.`,
            bookingId: data?.bookingId || `NA`,
            facilityName: data?.facility?.name || `NA`,
            name: data?.user?.name || `NA`,
            mobile: data?.user?.mobile || `NA`,
            email: data?.user?.email || `NA`,
            fees: data?.paymentSummary?.fees?.[0]?.value
              ? `${data?.paymentSummary?.fees?.[0]?.value} (${data?.paymentSummary?.fees?.[0]?.currency})`
              : `NA`,
            totalPayable: data?.paymentSummary?.totalPayable?.value
              ? `${data?.paymentSummary?.totalPayable?.value} (${data?.paymentSummary?.totalPayable?.currency})`
              : `NA`,
            dicounts: data?.paymentSummary?.discounts?.[0]?.amount
              ? `${data?.paymentSummary?.discounts?.[0]?.amount} (${data?.paymentSummary?.discounts?.[0]?.currency})`
              : `NA`,
            benefitType: Constant?.benefitType[data?.benefitType ?? 0] || `NA`,
            partnerName: data?.partner?.name || `NA`,
            serviceName: data?.service?.name || `NA`,
            paymentId: data?.payment?.paymentId || `NA`,
            membershipId: data?.membershipId || `NA`,
            createdAt: data?.createdAt ? moment(new Date(`${data?.createdAt}`)).format('DD-MM-YYYY, h:mm A') : 'NA',
            updatedAt: data?.updatedAt ? moment(new Date(`${data?.updatedAt}`)).format('DD-MM-YYYY, h:mm A') : 'NA',
            paymentStatus: data?.payment?.status,
            bookingStatus: status,
            data,
          };
        }),
      );
    } else {
      setData([]);
    }
  }, [props?.data]);

  /* ---------------------------- BOOKING TABLE TITLE HANDLER ---------------------------- */
  const getBookingStatusTitleHandler = bookingsStatusCode => {
    let bookingsTitle = 'Bookings';

    if (bookingsStatusCode !== 0) {
      Constant?.BOOKING_STATUSES?.forEach(bookingStatus => {
        if (bookingsStatusCode === bookingStatus?.objectId) {
          bookingsTitle = `${bookingStatus?.name} Bookings`;
        }
      });
    }
    setCurrentFilterBookingTitle(bookingsTitle);
  };

  return (
    <>
      <Row>
        <Col xxl={12}>
          <Card>
            <CardHeader className="d-flex">
              <h4 className="card-title mb-0 flex-grow-1">{currentFilteredBookingTitle}</h4>

              {hasPermission(PermissionConstants?.ALL_PERMISSIONS?.DOWNLOAD_ACCESS) && props?.data?.length > 0 && (
                <span className="btn btn-success mb-1" onClick={props?.dumpDownload}>
                  <i className="ri-download-line me-1 text-white fs-14 align-middle"></i>Download dump
                </span>
              )}
            </CardHeader>

            <CardBody>
              <DataTable
                columns={columns ?? []}
                data={data ?? []}
                highlightOnHover
                customStyles={{
                  headRow: {
                    style: {
                      borderBottom: '1px solid #ddd',
                      backgroundColor: '#f9fafb',
                      fontWeight: '600',
                    },
                  },
                  cells: {
                    style: {
                      border: '0.5px solid #ddd',
                      borderBottom: '0px solid #ddd',
                    },
                  },
                }}
              />
            </CardBody>

            {props?.data?.length > 0 && (
              <CardFooter>
                <Row className="custom_pagination">
                  <Col md={12} className="d-flex justify-content-between align-items-center">
                    <div>
                      Showing <b>{formattedWithComma(props?.pageNumber * Constant?.TABLE_PAGE_SIZE + 1)}</b> to&nbsp;
                      <b>{formattedWithComma(props?.pageNumber * Constant?.TABLE_PAGE_SIZE + props?.data?.length)}</b> of{' '}
                      <b>{formattedWithComma(props?.totalData)}</b> records
                    </div>
                    <div className="d-flex justify-content-end">
                      <TablePagination totalData={props?.totalData} currentPage={props?.pageNumber} onPageChangeHandler={props?.pageHandler} />
                    </div>
                  </Col>
                </Row>
              </CardFooter>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default React.memo(Table);
